/**
 * Vorausschauende Klangplanung ("A Tale of Two Clocks").
 *
 * Der Scheduler plant Audio-Ereignisse anhand der AudioContext-Zeit im Voraus.
 * Ein regelmäßiger Tick (Ticker) dient ausschließlich dazu, das nächste
 * Zeitfenster nachzuladen – er ist **nicht** die Zeitquelle der Wiedergabe.
 * Dadurch bleibt das Timing stabil, auch wenn der Tick unregelmäßig feuert.
 */

import type { AudioEngine } from './engine';
import type { AudioEvent } from './schedule';
import type { DrumInstrument } from '../model/types';

/** Regelmäßiger Auslöser zum Nachladen des Planungsfensters. */
export interface Ticker {
  start(callback: () => void): void;
  stop(): void;
}

/** Standard-Ticker auf Basis von `setInterval` (reines Nachlade-Signal). */
export class IntervalTicker implements Ticker {
  readonly #intervalMs: number;
  #handle: ReturnType<typeof setInterval> | null = null;

  constructor(intervalMs = 25) {
    this.#intervalMs = intervalMs;
  }

  start(callback: () => void): void {
    this.stop();
    this.#handle = setInterval(callback, this.#intervalMs);
  }

  stop(): void {
    if (this.#handle !== null) {
      clearInterval(this.#handle);
      this.#handle = null;
    }
  }
}

/** Vorausplanungsfenster in Sekunden. */
const DEFAULT_LOOKAHEAD = 0.12;
/** Kleiner Vorlauf, damit der erste Schlag nicht "zu spät" geplant wird. */
const START_OFFSET = 0.06;
/** Sicherheitsgrenze gegen pathologische Endlosschleifen im Pump. */
const MAX_ITERATIONS_PER_PUMP = 10000;

export type SampleMap = ReadonlyMap<DrumInstrument, AudioBuffer>;

export class Scheduler {
  readonly #engine: AudioEngine;
  readonly #samples: SampleMap;
  readonly #ticker: Ticker;
  readonly #lookahead: number;

  #events: readonly AudioEvent[] = [];
  #patternDuration = 0;
  #loop = false;
  #running = false;

  /** Kontextzeit, zu der der aktuelle Durchlauf beginnt. */
  #cycleStart = 0;
  /** Kontextzeit, zu der der gesamte Vorgang gestartet wurde. */
  #startTime = 0;
  /** Index des nächsten noch zu planenden Ereignisses im aktuellen Durchlauf. */
  #cursor = 0;

  constructor(
    engine: AudioEngine,
    samples: SampleMap,
    ticker: Ticker,
    lookahead: number = DEFAULT_LOOKAHEAD,
  ) {
    this.#engine = engine;
    this.#samples = samples;
    this.#ticker = ticker;
    this.#lookahead = lookahead;
  }

  get running(): boolean {
    return this.#running;
  }

  /** Kontextzeit des Wiedergabestarts. */
  get startTime(): number {
    return this.#startTime;
  }

  /** Vergangene Zeit seit Wiedergabestart, in Sekunden (Audiozeit). */
  elapsed(): number {
    return this.#engine.currentTime - this.#startTime;
  }

  /** Startet die Planung eines Patterns. */
  start(
    events: readonly AudioEvent[],
    patternDuration: number,
    loop: boolean,
  ): void {
    this.#events = events;
    this.#patternDuration = patternDuration;
    this.#loop = loop;
    this.#startTime = this.#engine.currentTime + START_OFFSET;
    this.#cycleStart = this.#startTime;
    this.#cursor = 0;
    this.#running = true;
    this.pump();
    this.#ticker.start(() => this.pump());
  }

  /** Beendet die Planung und stoppt alle Klänge. */
  stop(): void {
    this.#running = false;
    this.#ticker.stop();
    this.#engine.stopAll();
  }

  /** Schaltet den Loop-Modus um (wirkt ab dem nächsten Durchlaufwechsel). */
  setLoop(loop: boolean): void {
    this.#loop = loop;
  }

  get loop(): boolean {
    return this.#loop;
  }

  /**
   * Plant alle Ereignisse, deren Startzeit innerhalb des Vorausfensters liegt.
   * Bei aktivem Loop wird nahtlos in den nächsten Durchlauf übergegangen.
   */
  pump(): void {
    if (!this.#running) {
      return;
    }
    const horizon = this.#engine.currentTime + this.#lookahead;

    for (let guard = 0; guard < MAX_ITERATIONS_PER_PUMP; guard += 1) {
      if (this.#cursor < this.#events.length) {
        const event = this.#events[this.#cursor]!;
        const at = this.#cycleStart + event.time;
        if (at > horizon) {
          return;
        }
        const buffer = this.#samples.get(event.instrument);
        if (buffer) {
          this.#engine.play(buffer, at, event.gain);
        }
        this.#cursor += 1;
        continue;
      }

      // Ende des aktuellen Durchlaufs erreicht.
      if (!this.#loop || this.#patternDuration <= 0) {
        return;
      }
      const nextCycleStart = this.#cycleStart + this.#patternDuration;
      if (nextCycleStart > horizon) {
        return;
      }
      this.#cycleStart = nextCycleStart;
      this.#cursor = 0;
    }
  }
}
